"use client"

import { NewTabLink } from "./new-tab-link"
import { ResourceLink } from "./resource-link"
import { useEffect, useMemo, useRef, useState } from "react"
import { AlertTriangle, CheckCircle2, CircleSlash, Clock3, ExternalLink, Hourglass, Pause, Play, Trash2 } from "lucide-react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  DEADLINES_REVIEWED, describeRemaining, percentElapsed, resolveAllClocks,
  type ClockState, type Obligation, type ResolvedClock,
} from "@/lib/breach/deadlines"
import {
  addCustomClock, pauseClock, removeCustomClock, resumeClock, setClockOverride,
  type ClockOverride, type CustomClock, type Workspace,
} from "@/lib/breach/workspace"
import { openQuestions, shown, withAnswer, type Intake, type OpenQuestion, type QuestionId, type YesNoUnknown } from "@/lib/breach/intake"
import { reportAnchor, reportDestinationsFor } from "@/lib/breach/directory/refs"
import type { GoTo } from "./incident-nav"
import { OpenQuestionsPanel } from "./open-questions"
import { YNU } from "./choice"
import type { WorkspaceUpdater } from "./incident-tools"

const HOUR_MS = 3_600_000
const TICK_MS = 30_000

/** Ticking "now". Null until mount so server and first client render match. */
function useNow(intervalMs = TICK_MS): Date | null {
  const [now, setNow] = useState<Date | null>(null)
  useEffect(() => {
    setNow(new Date())
    const t = window.setInterval(() => setNow(new Date()), intervalMs)
    return () => window.clearInterval(t)
  }, [intervalMs])
  return now
}

// datetime-local <-> ISO conversions in local time (same pattern as the intake wizard)
const toIso = (local: string) => (local ? new Date(local).toISOString() : "")
function isoToLocal(iso: string | undefined): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}
const nowLocal = () => {
  const d = new Date()
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}
const validDate = (iso: string): Date | null => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}
const fmtDateTime = (d: Date) => d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })

function describeElapsedSince(from: Date, now: Date): string {
  const mins = Math.max(0, Math.floor((now.getTime() - from.getTime()) / 60_000))
  const days = Math.floor(mins / 1440)
  const hours = Math.floor((mins % 1440) / 60)
  const minutes = mins % 60
  const parts: string[] = []
  if (days) parts.push(`${days} day${days === 1 ? "" : "s"}`)
  if (hours) parts.push(`${hours} hour${hours === 1 ? "" : "s"}`)
  parts.push(`${minutes} minute${minutes === 1 ? "" : "s"}`)
  return parts.join(" ")
}

type Urgency = "overdue" | "soon" | "ok" | "unknown" | "no-date"

function urgencyOf(due: Date | null, now: Date | null): Urgency {
  if (!due) return "no-date"
  if (!now) return "unknown"
  const ms = due.getTime() - now.getTime()
  if (ms < 0) return "overdue"
  if (ms < 24 * HOUR_MS) return "soon"
  return "ok"
}

const cardTone: Record<Urgency, string> = {
  overdue: "border-destructive/60 bg-destructive/10",
  soon: "border-yellow-500/60 bg-yellow-500/10",
  ok: "border-border bg-card",
  unknown: "border-border bg-card",
  "no-date": "border-border bg-card",
}
const countdownTone: Record<Urgency, string> = {
  overdue: "text-destructive",
  soon: "text-amber-600 dark:text-amber-400",
  ok: "text-foreground",
  unknown: "text-foreground",
  "no-date": "text-foreground",
}
const barTone: Record<Urgency, string> = {
  overdue: "bg-destructive",
  soon: "bg-yellow-500",
  ok: "bg-green-500",
  unknown: "bg-green-500",
  "no-date": "bg-green-500",
}

/** Overdue first, then running by soonest due, then paused, waiting, not-triggered, no-date. */
const STATE_TIER: Record<ClockState, number> = {
  running: 1, paused: 2, waiting: 3, "not-triggered": 4, "no-date": 5,
}
function sortClocks(list: ResolvedClock[], now: Date | null): ResolvedClock[] {
  const t = now?.getTime()
  const tier = (c: ResolvedClock): number => {
    if (c.state === "running" && t !== undefined && c.date && c.date.getTime() < t) return 0
    return STATE_TIER[c.state]
  }
  return [...list].sort((a, b) => {
    const ta = tier(a), tb = tier(b)
    if (ta !== tb) return ta - tb
    const ad = a.date?.getTime()
    const bd = b.date?.getTime()
    if (ad === undefined) return bd === undefined ? 0 : 1
    if (bd === undefined) return -1
    return ad - bd
  })
}

/** Where the shown window starts: the override's start, else the timestamp the
 *  computed date was counted from (confirmed / noticed), else any timestamp we have. */
function clockStartFor(c: ResolvedClock, intake: Intake): Date | null {
  if (c.source === "lawyer-start" && c.override?.startIso) return validDate(c.override.startIso)
  if (c.basis === "confirmed" && intake.confirmedAt) return validDate(intake.confirmedAt)
  return validDate(intake.noticedAt) ?? validDate(intake.confirmedAt)
}

/** The two answers a data-breach clock waits on. Safely typed for any QuestionId. */
function gateValue(i: Intake, id: QuestionId): YesNoUnknown | null {
  if (id === "unauthorizedAccess" || id === "dataTaken") return shown(i, id)
  return null
}
function answerGateQuestion(update: WorkspaceUpdater, id: QuestionId, v: YesNoUnknown): void {
  if (id !== "unauthorizedAccess" && id !== "dataTaken") return
  update((x) => ({ ...x, updatedAt: new Date().toISOString(), intake: withAnswer(x.intake, id, v) }))
}

function UrgencyChip({ u }: { u: Urgency }) {
  if (u === "overdue") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-destructive/15 px-1.5 py-0.5 text-xs font-semibold text-destructive">
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden /> Past due
      </span>
    )
  }
  if (u === "soon") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-yellow-500/15 px-1.5 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-400">
        <Clock3 className="h-3.5 w-3.5" aria-hidden /> Due soon
      </span>
    )
  }
  if (u === "ok") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-green-500/15 px-1.5 py-0.5 text-xs font-semibold text-green-700 dark:text-green-400">
        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> On track
      </span>
    )
  }
  return null
}

function StatusBadge({ status }: { status: Obligation["status"] }) {
  if (status === "in-force") return null
  const label = status === "not-in-force" ? "Not yet in force" : "Rules in transition"
  return (
    <span className="inline-flex shrink-0 items-center rounded border border-yellow-500/60 px-1.5 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-400">
      {label}
    </span>
  )
}

function ClockHeader({ o }: { o: Obligation }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-sm font-semibold leading-snug">{o.regime}</p>
        <p className="text-xs text-muted-foreground">{o.citation}</p>
      </div>
      <StatusBadge status={o.status} />
    </div>
  )
}

function ClockDetails({ o }: { o: Obligation }) {
  const [notifyOpen, setNotifyOpen] = useState(false)
  return (
    <>
      <div className="mt-2 text-sm">
        <p className={cn("text-muted-foreground", !notifyOpen && "line-clamp-2 print:line-clamp-none")}>
          <span className="font-medium text-foreground">Who is notified: </span>{o.notify}
        </p>
        {o.notify.length > 120 && (
          <button type="button" aria-expanded={notifyOpen} onClick={() => setNotifyOpen((v) => !v)}
            className="mt-0.5 rounded text-xs font-medium text-muted-foreground underline hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-solid print:hidden">
            {notifyOpen ? "Less" : "More"}
          </button>
        )}
      </div>
      <p className="mt-2 text-xs text-muted-foreground"><span className="font-medium text-foreground">Clock starts:</span> {o.clockStarts}</p>
      {o.conditions && <p className="mt-1 text-xs text-muted-foreground">{o.conditions}</p>}
      {o.status !== "in-force" && <p className="mt-1 text-xs text-muted-foreground">{o.statusNote}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <a href={o.sourceUrl} target="_blank" rel="noopener"
          className="inline-flex items-center gap-1 text-muted-foreground underline hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-solid">
          Source <ExternalLink className="h-3 w-3" aria-hidden />
        </a>
        <span className="text-muted-foreground">Penalties may apply for missed required notices.</span>
      </div>
      {reportDestinationsFor(o.id).length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground print:text-foreground">
          <span className="font-medium text-foreground">Where to file:</span>{" "}
          {reportDestinationsFor(o.id).slice(0, 3).map((d, i) => (
            <span key={d.id}>{i > 0 && " · "}<ResourceLink href={reportAnchor(d.id)} className="underline hover:text-foreground">{d.agency}</ResourceLink></span>
          ))}
          {" · "}<ResourceLink href="/directory/report" className="underline hover:text-foreground">All reporting contacts</ResourceLink>
        </p>
      )}
    </>
  )
}

function PauseHistory({ pauses }: { pauses: NonNullable<ClockOverride["pauses"]> }) {
  if (pauses.length === 0) return null
  return (
    <div className="grid gap-1">
      <p className="text-xs font-medium">Pause history</p>
      <ul className="grid gap-0.5 text-xs text-muted-foreground">
        {pauses.map((p, idx) => {
          const from = validDate(p.fromIso)
          const to = p.toIso ? validDate(p.toIso) : null
          return (
            <li key={idx}>
              {from ? fmtDateTime(from) : "Unknown start"} →{" "}
              {p.toIso ? (to ? fmtDateTime(to) : "unknown end") : <span className="font-medium text-foreground">still paused</span>}
              {p.reason ? ` · ${p.reason}` : ""}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function AdjustPanel({ o, ov, update, onClose }: {
  o: Obligation; ov: ClockOverride | undefined; update: WorkspaceUpdater; onClose: () => void
}) {
  return (
    <div className="grid gap-2 rounded-md border border-border bg-background/60 p-3">
      <label className="grid gap-1 text-xs">
        Due date and time per my lawyer
        <input type="datetime-local" className="rounded border border-border bg-background px-2 py-1 text-sm"
          value={isoToLocal(ov?.dueIso)}
          onChange={(e) => update((x) => setClockOverride(x, o.id, { dueIso: toIso(e.target.value) }))} />
      </label>
      {o.calc && (
        <label className="grid gap-1 text-xs">
          Legal clock started at
          <input type="datetime-local" className="rounded border border-border bg-background px-2 py-1 text-sm"
            value={isoToLocal(ov?.startIso)}
            onChange={(e) => update((x) => setClockOverride(x, o.id, { startIso: toIso(e.target.value) }))} />
        </label>
      )}
      <label className="grid gap-1 text-xs">
        Note
        <input type="text" placeholder="e.g., Per Jane Smith, outside counsel" className="rounded border border-border bg-background px-2 py-1 text-sm"
          value={ov?.note ?? ""}
          onChange={(e) => update((x) => setClockOverride(x, o.id, { note: e.target.value }))} />
      </label>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" className="h-4 w-4" checked={!!ov?.hidden}
          onChange={(e) => update((x) => setClockOverride(x, o.id, { hidden: e.target.checked }))} />
        My lawyer says this doesn&apos;t apply
      </label>
      {ov?.pauses && <PauseHistory pauses={ov.pauses} />}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => update((x) => setClockOverride(x, o.id, null))}>Reset to computed</Button>
        <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
      </div>
    </div>
  )
}

/** running / paused / no-date: a dated countdown plus pause, resume, and adjust. */
function DatedClockCard({ clock, w, update, now }: {
  clock: ResolvedClock; w: Workspace; update: WorkspaceUpdater; now: Date | null
}) {
  const o = clock.obligation
  const due = clock.date
  const u = urgencyOf(due, now)
  const rem = due && now ? describeRemaining(due, now) : null
  const [adjusting, setAdjusting] = useState(false)
  const [pauseOpen, setPauseOpen] = useState(false)
  const [pauseReason, setPauseReason] = useState("")
  const ov = clock.override
  const paused = clock.state === "paused"
  const openPause = ov?.pauses?.find((p) => !p.toIso)
  const pct = useMemo(() => {
    if (!now || !due) return null
    return percentElapsed(clockStartFor(clock, w.intake), due, now)
  }, [clock, w.intake, due, now])

  return (
    <div className={cn("rounded-lg border p-3", cardTone[u])}>
      <ClockHeader o={o} />

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span role="timer" aria-live="off" className={cn("text-2xl font-bold tabular-nums", countdownTone[u])}>
          {due ? (rem?.text ?? "…") : "No date yet"}
        </span>
        {paused && (
          <span className="inline-flex items-center gap-1 rounded bg-yellow-500/15 px-1.5 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-400">
            <Pause className="h-3.5 w-3.5" aria-hidden /> Paused
          </span>
        )}
        {paused && rem && <span className="text-xs font-medium text-muted-foreground">Frozen</span>}
        <UrgencyChip u={u} />
        {(clock.source === "lawyer-due" || clock.source === "lawyer-start") && (
          <span className="rounded border border-accent-solid/50 bg-accent/10 px-1.5 py-0.5 text-xs font-medium text-accent-solid">Set by you</span>
        )}
      </div>
      {due && <p className="text-sm text-muted-foreground">Due {fmtDateTime(due)}</p>}
      {paused && now && (
        <p className="mt-0.5 text-xs text-muted-foreground">
          Paused for {describeElapsedSince(new Date(now.getTime() - clock.pausedMs), now)}
          {clock.pausedMs > 0 && <span> (total across pauses)</span>}
        </p>
      )}
      {paused && openPause?.reason && (
        <p className="mt-0.5 text-xs"><span className="font-medium">Reason:</span> <span className="text-muted-foreground">{openPause.reason}</span></p>
      )}
      {clock.note && <p className="mt-0.5 text-xs text-muted-foreground">{clock.note}</p>}
      {ov?.note && (
        <p className="mt-0.5 text-xs"><span className="font-medium">Your note:</span> <span className="text-muted-foreground">{ov.note}</span></p>
      )}

      {pct !== null && (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
          <div className={cn("h-full rounded-full", barTone[u])} style={{ width: `${pct}%` }} />
        </div>
      )}

      <ClockDetails o={o} />

      <div className="mt-2 flex flex-wrap gap-2 print:hidden">
        {clock.state === "running" && !pauseOpen && (
          <Button variant="outline" size="sm" onClick={() => setPauseOpen(true)}>Pause</Button>
        )}
        {paused && (
          <Button size="sm" onClick={() => update((x) => resumeClock(x, o.id))}>
            <Play aria-hidden /> Resume
          </Button>
        )}
        {!adjusting && (
          <Button variant="outline" size="sm" aria-expanded={adjusting} onClick={() => setAdjusting(true)}>Adjust</Button>
        )}
      </div>

      {pauseOpen && clock.state === "running" && (
        <form className="mt-2 grid gap-2 rounded-md border border-border bg-background/60 p-3 print:hidden"
          onSubmit={(e) => {
            e.preventDefault()
            update((x) => pauseClock(x, o.id, pauseReason))
            setPauseOpen(false)
            setPauseReason("")
          }}>
          <label className="grid gap-1 text-xs">
            Reason (optional)
            <input type="text" placeholder="Why? For example: law enforcement asked us to delay"
              className="rounded border border-border bg-background px-2 py-1 text-sm"
              value={pauseReason} onChange={(e) => setPauseReason(e.target.value)} />
          </label>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm">Confirm pause</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setPauseOpen(false)}>Cancel</Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Pausing is your decision. Most legal deadlines don&apos;t stop on their own. Confirm with your lawyer first.
          </p>
        </form>
      )}

      {adjusting && (
        <div className="mt-2 print:hidden">
          <AdjustPanel o={o} ov={ov} update={update} onClose={() => setAdjusting(false)} />
        </div>
      )}
    </div>
  )
}

/** waiting: the clock can't start until the user knows someone got in / data was taken.
 *  No countdown is shown; the gating questions are answered inline. */
function WaitingClockCard({ clock, w, update }: {
  clock: ResolvedClock; w: Workspace; update: WorkspaceUpdater
}) {
  const o = clock.obligation
  const ov = clock.override
  const [adjusting, setAdjusting] = useState(false)
  const [starting, setStarting] = useState(false)
  const [startLocal, setStartLocal] = useState("")
  const questions = useMemo(
    () => new Map<QuestionId, OpenQuestion>(openQuestions(w.intake).map((q) => [q.id, q] as const)),
    [w.intake],
  )

  const beginStart = () => {
    setStartLocal(isoToLocal(w.intake.noticedAt) || nowLocal())
    setStarting(true)
  }

  return (
    <div className="rounded-lg border border-dashed border-border bg-card p-3">
      <ClockHeader o={o} />

      <div className="mt-2">
        <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs font-semibold text-muted-foreground">
          <Hourglass className="h-3.5 w-3.5" aria-hidden /> Not started
        </span>
      </div>

      <h3 className="mt-2 text-sm font-semibold">Waiting for what you know</h3>
      <ul className="mt-1 grid gap-3">
        {clock.waitingOn.map((id) => {
          const q = questions.get(id)
          const current = gateValue(w.intake, id)
          return (
            <li key={id} className="grid gap-1.5">
              <p className="text-sm font-medium leading-snug">{q?.prompt ?? id}</p>
              {q?.why && <p className="text-xs text-muted-foreground">{q.why}</p>}
              <div className="flex flex-wrap gap-2 print:hidden" role="group" aria-label={q?.prompt ?? id}>
                {YNU.map((opt) => {
                  const selected = current === opt.id
                  return (
                    <Button key={opt.id} size="sm" aria-pressed={selected}
                      variant={selected ? "default" : "outline"}
                      className={selected ? "bg-accent-solid text-accent-solid-foreground hover:bg-accent-solid/90" : undefined}
                      onClick={() => answerGateQuestion(update, id, opt.id)}>
                      {opt.label}
                    </Button>
                  )
                })}
              </div>
            </li>
          )
        })}
      </ul>

      {clock.note && <p className="mt-2 text-xs text-muted-foreground">{clock.note}</p>}
      <ClockDetails o={o} />

      <div className="mt-2 print:hidden">
        {!starting ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" size="sm" aria-expanded={starting} onClick={beginStart}>
              Start it now anyway
            </Button>
            {!adjusting && (
              <Button variant="outline" size="sm" aria-expanded={adjusting} onClick={() => setAdjusting(true)}>Adjust</Button>
            )}
          </div>
        ) : (
          <form className="grid gap-2 rounded-md border border-border bg-background/60 p-3"
            onSubmit={(e) => {
              e.preventDefault()
              if (!startLocal) return
              update((x) => setClockOverride(x, o.id, { startIso: toIso(startLocal) }))
              setStarting(false)
            }}>
            <label className="grid gap-1 text-xs">
              Legal clock started at
              <input type="datetime-local" className="rounded border border-border bg-background px-2 py-1 text-sm"
                value={startLocal} onChange={(e) => setStartLocal(e.target.value)} />
            </label>
            <p className="text-xs text-muted-foreground">
              Use this when your lawyer says the legal clock has already started. Saving it starts the clock and stops the questions above from holding it.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" size="sm">Save and start</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setStarting(false)}>Cancel</Button>
            </div>
          </form>
        )}
        {adjusting && (
          <div className="mt-2">
            <AdjustPanel o={o} ov={ov} update={update} onClose={() => setAdjusting(false)} />
          </div>
        )}
      </div>
    </div>
  )
}

/** not-triggered: the user said nobody got in without permission. */
function NotTriggeredClockCard({ clock, update }: { clock: ResolvedClock; update: WorkspaceUpdater }) {
  const o = clock.obligation
  const ov = clock.override
  const [adjusting, setAdjusting] = useState(false)
  return (
    <div className="rounded-lg border border-border bg-muted/40 p-3">
      <ClockHeader o={o} />

      <div className="mt-2">
        <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs font-semibold text-muted-foreground">
          <CircleSlash className="h-3.5 w-3.5" aria-hidden /> Probably not triggered
        </span>
      </div>

      {clock.note && <p className="mt-2 text-xs text-muted-foreground">{clock.note}</p>}
      <ClockDetails o={o} />

      <div className="mt-2 print:hidden">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => answerGateQuestion(update, "unauthorizedAccess", "yes")}>
            Actually, someone did get in
          </Button>
          <Button size="sm" variant="ghost" onClick={() => answerGateQuestion(update, "unauthorizedAccess", "unknown")}>
            I&apos;m still not sure
          </Button>
          {!adjusting && (
            <Button variant="outline" size="sm" aria-expanded={adjusting} onClick={() => setAdjusting(true)}>Adjust</Button>
          )}
        </div>
        {adjusting && (
          <div className="mt-2">
            <AdjustPanel o={o} ov={ov} update={update} onClose={() => setAdjusting(false)} />
          </div>
        )}
      </div>
    </div>
  )
}

function ClockCard({ clock, w, update, now }: {
  clock: ResolvedClock; w: Workspace; update: WorkspaceUpdater; now: Date | null
}) {
  if (clock.state === "waiting") return <WaitingClockCard clock={clock} w={w} update={update} />
  if (clock.state === "not-triggered") return <NotTriggeredClockCard clock={clock} update={update} />
  return <DatedClockCard clock={clock} w={w} update={update} now={now} />
}

function CustomClockCard({ c, update, now }: {
  c: CustomClock; update: WorkspaceUpdater; now: Date | null
}) {
  const due = validDate(c.dueIso)
  const u = urgencyOf(due, now)
  const rem = due && now ? describeRemaining(due, now) : null
  return (
    <div className={cn("rounded-lg border p-3", cardTone[u])}>
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 text-sm font-semibold leading-snug">{c.label}</p>
        <button type="button" aria-label={`Remove clock: ${c.label}`}
          className="shrink-0 rounded text-muted-foreground hover:text-destructive focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-solid print:hidden"
          onClick={() => update((x) => removeCustomClock(x, c.id))}>
          <Trash2 className="h-4 w-4" aria-hidden />
        </button>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span role="timer" aria-live="off" className={cn("text-xl font-bold tabular-nums", countdownTone[u])}>
          {due ? (rem?.text ?? "…") : "No date yet"}
        </span>
        <UrgencyChip u={u} />
      </div>
      {due && <p className="text-sm text-muted-foreground">Due {fmtDateTime(due)}</p>}
      {c.note && <p className="mt-0.5 text-xs text-muted-foreground">{c.note}</p>}
    </div>
  )
}

function AddCustomClock({ update }: { update: WorkspaceUpdater }) {
  const [label, setLabel] = useState("")
  const [due, setDue] = useState("")
  const [note, setNote] = useState("")
  return (
    <form className="grid gap-2 rounded-lg border border-border bg-card p-3 print:hidden"
      onSubmit={(e) => {
        e.preventDefault()
        if (!label.trim() || !due) return
        update((x) => addCustomClock(x, label, toIso(due), note))
        setLabel(""); setDue(""); setNote("")
      }}>
      <p className="text-sm font-semibold">Add your own clock</p>
      <input className="rounded border border-border bg-background px-2 py-1 text-sm" placeholder="Name this clock"
        aria-label="Clock name" value={label} onChange={(e) => setLabel(e.target.value)} />
      <label className="grid gap-1 text-xs">
        Due date and time
        <input type="datetime-local" className="rounded border border-border bg-background px-2 py-1 text-sm"
          aria-label="Clock due date and time" value={due} onChange={(e) => setDue(e.target.value)} />
      </label>
      <input className="rounded border border-border bg-background px-2 py-1 text-sm" placeholder="Note (optional)"
        aria-label="Clock note" value={note} onChange={(e) => setNote(e.target.value)} />
      <p className="text-xs text-muted-foreground">Examples: insurer notice window · customer contract notice · a date my lawyer gave me</p>
      <Button type="submit" size="sm">Add clock</Button>
    </form>
  )
}

function ClocksContent({ w, update, now }: { w: Workspace; update: WorkspaceUpdater; now: Date | null }) {
  const clocks = useMemo(() => resolveAllClocks(w.intake, w.clockOverrides), [w.intake, w.clockOverrides])
  const visible = useMemo(() => sortClocks(clocks.filter((c) => !c.hidden), now), [clocks, now])
  const hidden = clocks.filter((c) => c.hidden)
  const customs = useMemo(
    () => [...w.customClocks].sort((a, b) => (validDate(a.dueIso)?.getTime() ?? Infinity) - (validDate(b.dueIso)?.getTime() ?? Infinity)),
    [w.customClocks],
  )
  const noticed = validDate(w.intake.noticedAt)

  const counts = useMemo(() => {
    const c = { running: 0, waiting: 0, paused: 0 }
    for (const clock of visible) {
      if (clock.state === "running") c.running++
      else if (clock.state === "waiting") c.waiting++
      else if (clock.state === "paused") c.paused++
    }
    return c
  }, [visible])
  const summary = [
    counts.running > 0 && `${counts.running} running`,
    counts.waiting > 0 && `${counts.waiting} waiting`,
    counts.paused > 0 && `${counts.paused} paused`,
  ].filter(Boolean).join(" · ")

  const setIntakeTime = (key: "noticedAt" | "confirmedAt", local: string) => {
    const value = toIso(local)
    update((x) => ({ ...x, updatedAt: new Date().toISOString(), intake: { ...x.intake, [key]: value } }))
  }

  return (
    <section className="grid gap-3" aria-label="Deadline clocks">
      <div>
        <p className="text-base font-semibold">Deadline clocks</p>
        {summary && <p className="mt-0.5 text-xs text-muted-foreground">{summary}</p>}
        {!DEADLINES_REVIEWED && (
          <p className="mt-0.5 text-xs text-muted-foreground">Draft estimates. Confirm every deadline with your lawyer.</p>
        )}
      </div>

      <div className="rounded-lg border border-border bg-card p-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Since you noticed it</p>
        <p role="timer" aria-live="off" className="mt-1 text-lg font-bold tabular-nums">
          {noticed && now ? describeElapsedSince(noticed, now) : "—"}
        </p>
        {!noticed && <p className="mt-1 text-xs text-muted-foreground">Enter when you first noticed it to start the clocks.</p>}
        <label className="mt-2 grid gap-1 text-xs">
          First noticed
          <input type="datetime-local" className="rounded border border-border bg-background px-2 py-1 text-sm"
            value={isoToLocal(w.intake.noticedAt)} onChange={(e) => setIntakeTime("noticedAt", e.target.value)} />
        </label>
        <label className="mt-2 grid gap-1 text-xs">
          Confirmed it was an incident
          <input type="datetime-local" className="rounded border border-border bg-background px-2 py-1 text-sm"
            value={isoToLocal(w.intake.confirmedAt)} onChange={(e) => setIntakeTime("confirmedAt", e.target.value)} />
        </label>
      </div>

      {visible.map((c) => (
        <ClockCard key={c.obligation.id} clock={c} w={w} update={update} now={now} />
      ))}

      {clocks.length === 0 && customs.length === 0 && (
        <p className="rounded-lg border border-border bg-card p-3 text-xs text-muted-foreground">
          No federal deadlines flagged from your answers. State and non-US laws also apply and are not covered yet. Ask your lawyer. You can add your own clocks above/below.
        </p>
      )}
      {clocks.length > 0 && visible.length === 0 && (
        <p className="rounded-lg border border-border bg-card p-3 text-xs text-muted-foreground">
          All clocks are hidden. Show them again below.
        </p>
      )}

      {hidden.length > 0 && (
        <details className="rounded-lg border border-border bg-card p-3">
          <summary className="cursor-pointer text-sm font-medium">Hidden ({hidden.length})</summary>
          <ul className="mt-2 grid gap-2">
            {hidden.map((c) => (
              <li key={c.obligation.id} className="flex flex-wrap items-center justify-between gap-2 text-xs">
                <span>{c.obligation.regime}{c.override?.note ? <span className="text-muted-foreground"> — {c.override.note}</span> : null}</span>
                <Button size="sm" variant="outline" className="print:hidden"
                  onClick={() => update((x) => setClockOverride(x, c.obligation.id, { hidden: false }))}>
                  Show again
                </Button>
              </li>
            ))}
          </ul>
        </details>
      )}

      <AddCustomClock update={update} />
      {customs.map((c) => (
        <CustomClockCard key={c.id} c={c} update={update} now={now} />
      ))}

      <p className="text-xs text-muted-foreground">
        Dates are conservative estimates counted from the times you entered. Where a legal clock starts later (for example when you determine an incident occurred) the date shown can be earlier than the real deadline. Not legal advice.
      </p>
    </section>
  )
}

/**
 * The deadline column: on mobile a collapsible block above the main content
 * (auto-opened when something is overdue or under 24h), on lg+ a sticky
 * ~320px right-hand aside. The clocks also print. Open questions sit above
 * the clocks so the clock-gating answers are always one tap away.
 */
export function DeadlineColumn({ w, update, go }: { w: Workspace; update: WorkspaceUpdater; go: GoTo }) {
  const now = useNow()
  const clocks = useMemo(() => resolveAllClocks(w.intake, w.clockOverrides), [w.intake, w.clockOverrides])
  const total = useMemo(() => clocks.filter((c) => !c.hidden).length + w.customClocks.length, [clocks, w.customClocks])
  const anyUrgent = useMemo(() => {
    if (!now) return false
    const t = now.getTime()
    const dues = [
      ...clocks.filter((c) => !c.hidden).map((c) => c.date?.getTime() ?? null),
      ...w.customClocks.map((c) => validDate(c.dueIso)?.getTime() ?? null),
    ]
    return dues.some((d) => d !== null && d < t + 24 * HOUR_MS)
  }, [clocks, w.customClocks, now])

  const [open, setOpen] = useState(false)
  const initialized = useRef(false)
  useEffect(() => {
    if (initialized.current || !now) return
    initialized.current = true
    if (anyUrgent) setOpen(true)
  }, [anyUrgent, now])

  return (
    <>
      <details open={open} onToggle={(e) => setOpen(e.currentTarget.open)}
        className="mb-4 rounded-lg border border-border bg-card lg:hidden print:hidden">
        <summary className="cursor-pointer p-3 text-sm font-semibold">Deadline clocks ({total})</summary>
        <div className="grid gap-3 border-t border-border p-3">
          <OpenQuestionsPanel workspace={w} update={update} nav={go} variant="compact" />
          <ClocksContent w={w} update={update} now={now} />
        </div>
      </details>
      <aside aria-label="Deadline clocks" className="hidden lg:block print:block lg:col-start-2 lg:row-start-1">
        <div className="grid gap-3 lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto print:max-h-none print:overflow-visible">
          <OpenQuestionsPanel workspace={w} update={update} nav={go} variant="compact" />
          <ClocksContent w={w} update={update} now={now} />
        </div>
      </aside>
    </>
  )
}
